import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Plus, Upload, Download, Mail, Send, Trash2, UserPlus, CheckCircle2, Clock, X, Lock, Wallet } from "lucide-react";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const STATUS = {
  up_to_date: { label: "À jour", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  none: { label: "—", cls: "bg-slate-100 text-slate-500" },
};
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", membership_status: "none", membership_amount: "", membership_date: "", notes: "" };

export default function AssocMembers() {
  const ctx = useOutletContext() || {};
  const active = ctx.asso ? ctx.asso.status === "active" : true;
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_account: 0, invited_pending: 0, up_to_date: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [autoInvite, setAutoInvite] = useState(false);
  const [impBusy, setImpBusy] = useState(false);
  const [broadcast, setBroadcast] = useState(false);
  const [bc, setBc] = useState({ subject: "", message: "", only_members_with_account: false });
  const [bcBusy, setBcBusy] = useState(false);
  const [remindBusy, setRemindBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/associations/me/members"); setItems(data.items || []); setStats(data.stats || {}); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const addMember = async () => {
    if (!form.email || !form.email.includes("@")) { toast.error("Email valide requis."); return; }
    setBusy(true);
    try { await api.post("/associations/me/members", form); toast.success("Membre ajouté."); setForm(EMPTY); setAdding(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible."); }
    finally { setBusy(false); }
  };

  const setStatus = async (m, membership_status) => {
    try { await api.patch(`/associations/me/members/${m.id}`, { membership_status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Retirer ${m.email} de l'annuaire ?`)) return;
    try { await api.delete(`/associations/me/members/${m.id}`); toast.success("Membre retiré."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const invite = async (m) => {
    try { await api.post(`/associations/me/members/${m.id}/invite`); toast.success("Invitation envoyée."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const createAccount = async (m) => {
    try { const { data } = await api.post(`/associations/me/members/${m.id}/create-account`);
      toast.success(data.existing ? "Compte existant rattaché." : "Compte créé, identifiants envoyés."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const inviteAll = async () => {
    try { const { data } = await api.post("/associations/me/members/invite-pending");
      toast.success(`${data.sent} invitation(s) envoyée(s).`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const runImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choisissez un fichier CSV ou Excel."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 5 Mo)."); return; }
    setImpBusy(true);
    try {
      const content_base64 = await fileToDataUrl(f);
      const { data } = await api.post("/associations/me/members/import", { filename: f.name, content_base64, auto_invite: autoInvite });
      toast.success(`${data.added} ajouté(s), ${data.skipped} ignoré(s), ${data.errors} erreur(s).`);
      if (autoInvite && data.invited > 0) toast.success(`${data.invited} invitation(s) envoyée(s).`);
      setImporting(false); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible."); }
    finally { setImpBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/associations/me/members/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "membres.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };

  const sendBroadcast = async () => {
    if (!bc.subject || !bc.message) { toast.error("Objet et message requis."); return; }
    setBcBusy(true);
    try { const { data } = await api.post("/associations/me/members/broadcast", bc);
      toast.success(`Message envoyé à ${data.sent} membre(s).`); setBc({ subject: "", message: "", only_members_with_account: false }); setBroadcast(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setBcBusy(false); }
  };

  const remindTargets = items.filter((m) => ["pending", "none"].includes(m.membership_status || "none") && m.email).length;

  const sendReminders = async () => {
    if (remindTargets === 0) { toast.info("Aucune cotisation à relancer (tous les membres sont à jour)."); return; }
    if (!window.confirm(`Envoyer un rappel de cotisation à ${remindTargets} membre(s) « en attente » ou « non renseignée » ?`)) return;
    setRemindBusy(true);
    try { const { data } = await api.post("/associations/me/members/remind");
      toast.success(`${data.sent} rappel(s) de cotisation envoyé(s).`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setRemindBusy(false); }
  };

  return (
    <div className="max-w-5xl" data-testid="assoc-members">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Mes membres</h1>
          <p className="text-slate-500 text-sm mt-1">Gérez votre annuaire, les cotisations et les invitations.</p>
        </div>
      </div>

      {!active && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex items-center gap-2" data-testid="assoc-members-locked">
          <Lock size={16} /> La gestion complète des membres sera pleinement active dès la validation de votre page.
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Membres", stats.total], ["Comptes créés", stats.with_account], ["Invitations en attente", stats.invited_pending], ["Cotisations à jour", stats.up_to_date]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`assoc-members-stat-${l}`}>
            <div className="text-2xl font-extrabold text-emerald-700">{v ?? 0}</div>
            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => { setForm(EMPTY); setAdding(true); }} className="btn-shine text-white" data-testid="assoc-member-add-btn"><Plus size={16} className="mr-1" /> Ajouter un membre</Button>
        <Button variant="outline" onClick={() => setImporting(true)} data-testid="assoc-member-import-btn"><Upload size={16} className="mr-1" /> Importer (CSV / Excel)</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="assoc-member-export-btn"><Download size={16} className="mr-1" /> Exporter</Button>
        <Button variant="outline" onClick={inviteAll} data-testid="assoc-member-invite-all-btn"><Mail size={16} className="mr-1" /> Inviter les non-inscrits</Button>
        <Button variant="outline" onClick={() => setBroadcast(true)} data-testid="assoc-member-broadcast-btn"><Send size={16} className="mr-1" /> Email groupé</Button>
        <Button variant="outline" onClick={sendReminders} disabled={remindBusy} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="assoc-member-remind-btn">
          <Wallet size={16} className="mr-1" /> {remindBusy ? "Envoi…" : `Relancer les cotisations${remindTargets ? ` (${remindTargets})` : ""}`}
        </Button>
      </div>

      {adding && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="assoc-member-form">
          <div className="flex items-center justify-between"><h2 className="font-heading font-bold">Nouveau membre</h2><button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="assoc-member-firstname" /></div>
            <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="assoc-member-lastname" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="assoc-member-email" /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div>
              <Label>Cotisation</Label>
              <Select value={form.membership_status} onValueChange={(v) => set("membership_status", v)}>
                <SelectTrigger data-testid="assoc-member-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Non renseignée</SelectItem><SelectItem value="pending">En attente</SelectItem><SelectItem value="up_to_date">À jour</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Montant cotisation</Label><Input value={form.membership_amount} onChange={(e) => set("membership_amount", e.target.value)} placeholder="ex : 20 €" /></div>
            <div className="sm:col-span-2"><Label>Notes internes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAdding(false)}>Annuler</Button><Button onClick={addMember} disabled={busy} className="btn-shine text-white" data-testid="assoc-member-save">{busy ? "Ajout…" : "Ajouter"}</Button></div>
        </div>
      )}

      {importing && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="assoc-member-import-form">
          <div className="flex items-center justify-between"><h2 className="font-heading font-bold">Importer des membres</h2><button onClick={() => setImporting(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
          <p className="text-sm text-slate-500">Fichier CSV ou Excel. Colonnes reconnues : <b>email</b> (requis), prénom, nom, téléphone.</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="block text-sm" data-testid="assoc-member-import-file" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={autoInvite} onCheckedChange={setAutoInvite} data-testid="assoc-member-import-autoinvite" /> Inviter automatiquement par email après l'import</label>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setImporting(false)}>Annuler</Button><Button onClick={runImport} disabled={impBusy} className="btn-shine text-white" data-testid="assoc-member-import-run">{impBusy ? "Import…" : "Importer"}</Button></div>
        </div>
      )}

      {broadcast && (
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="assoc-member-broadcast-form">
          <div className="flex items-center justify-between"><h2 className="font-heading font-bold">Email groupé aux membres</h2><button onClick={() => setBroadcast(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
          <div><Label>Objet *</Label><Input value={bc.subject} onChange={(e) => setBc({ ...bc, subject: e.target.value })} data-testid="assoc-broadcast-subject" /></div>
          <div><Label>Message *</Label><Textarea rows={6} value={bc.message} onChange={(e) => setBc({ ...bc, message: e.target.value })} data-testid="assoc-broadcast-message" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={bc.only_members_with_account} onCheckedChange={(v) => setBc({ ...bc, only_members_with_account: v })} /> Uniquement les membres ayant un compte</label>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setBroadcast(false)}>Annuler</Button><Button onClick={sendBroadcast} disabled={bcBusy} className="btn-shine text-white" data-testid="assoc-broadcast-send">{bcBusy ? "Envoi…" : "Envoyer"}</Button></div>
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-500 text-sm">Chargement…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="assoc-members-empty">
            <Users size={30} className="mx-auto text-slate-300 mb-2" /> Aucun membre pour l'instant. Ajoutez-en un ou importez votre liste.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                <th className="text-left px-4 py-3">Membre</th><th className="text-left px-4 py-3">Cotisation</th>
                <th className="text-left px-4 py-3">Compte</th><th className="text-right px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((m) => {
                  const st = STATUS[m.membership_status] || STATUS.none;
                  return (
                    <tr key={m.id} className="border-t border-slate-100" data-testid={`assoc-member-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}</div>
                        <div className="text-xs text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={m.membership_status || "none"} onValueChange={(v) => setStatus(m, v)}>
                          <SelectTrigger className={`h-7 w-32 text-xs ${st.cls} border-0`} data-testid={`assoc-member-status-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="pending">En attente</SelectItem><SelectItem value="up_to_date">À jour</SelectItem></SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        {m.user_id ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 size={14} /> Actif</span>
                          : m.invite_status === "pending" ? <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-semibold"><Clock size={14} /> Invité</span>
                          : <span className="text-slate-400 text-xs">Aucun</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {!m.user_id && <Button variant="ghost" size="sm" title="Envoyer une invitation" onClick={() => invite(m)} data-testid={`assoc-member-invite-${m.id}`}><Mail size={15} /></Button>}
                          {!m.user_id && <Button variant="ghost" size="sm" title="Créer le compte" onClick={() => createAccount(m)} data-testid={`assoc-member-create-${m.id}`}><UserPlus size={15} /></Button>}
                          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => remove(m)} data-testid={`assoc-member-delete-${m.id}`}><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
